import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
} from "@transfr-inc/dashboard-components";
import {
  metricById,
  useMetricRegistry,
} from "@transfr-inc/dashboard-components/outcome-metrics";
import {
  formatDateLocaleMonth,
  formatDateWithTime,
} from "@transfr-inc/dashboard-components/utils";

/**
 * created_at comes back as a naive UTC string; pin it so the library's
 * UTC-rendering date formatters show the stored instant rather than a
 * local-parse shift.
 */
function asUtc(value) {
  return new Date(value.endsWith("Z") ? value : `${value}Z`);
}

/** "Impact Report — August 5, 2026" — mirrors the generated deck's Drive name. */
function deckLabel(typeName, createdAt) {
  return `${typeName} — ${formatDateLocaleMonth(asUtc(createdAt))}`;
}

function DeckHistoryRowSummary({ deck, typeName }) {
  const succeeded = deck.status === "succeeded";
  return (
    <div className="impact-report-deck-row-summary">
      {succeeded ? (
        <a
          href={deck.url}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          {deckLabel(typeName, deck.createdAt)}
          <FontAwesomeIcon icon="fa-regular fa-arrow-up-right-from-square" />
        </a>
      ) : (
        <span className="impact-report-deck-row-label">
          {deckLabel(typeName, deck.createdAt)}
          <span className="impact-report-deck-failed-badge">Failed</span>
        </span>
      )}
      <span className="impact-report-deck-row-meta">
        {formatDateWithTime(asUtc(deck.createdAt))} ·{" "}
        {deck.createdByName ?? "—"}
        {succeeded &&
          Number.isFinite(deck.durationMs) &&
          ` · ${Math.round(deck.durationMs / 1000)}s`}
      </span>
    </div>
  );
}

export function DeckHistoryRow({ deck, typeName }) {
  const registry = useMetricRegistry();
  const inputs = deck.inputs;

  if (!inputs) {
    return (
      <li className="impact-report-deck-row">
        <DeckHistoryRowSummary deck={deck} typeName={typeName} />
      </li>
    );
  }

  return (
    <li className="impact-report-deck-row">
      <Accordion className="impact-report-deck-accordion">
        <AccordionHeader>
          <AccordionHeaderName>
            <DeckHistoryRowSummary deck={deck} typeName={typeName} />
          </AccordionHeaderName>
        </AccordionHeader>
        <AccordionBody>
          <div className="impact-report-deck-inputs">
            <div className="impact-report-deck-inputs-row">
              <span className="impact-report-deck-inputs-label">Period</span>
              <span>
                {formatDateLocaleMonth(inputs.dateRange.start)} –{" "}
                {formatDateLocaleMonth(inputs.dateRange.end)}
              </span>
            </div>
            <div className="impact-report-deck-inputs-row">
              <span className="impact-report-deck-inputs-label">Metrics</span>
              <span className="impact-report-deck-metric-chips">
                {inputs.metrics.map((id) => (
                  <span key={id} className="impact-report-deck-metric-chip">
                    {metricById(registry, id)?.label ?? id}
                    {Number.isFinite(deck.metricValues?.[id]) && (
                      <strong>{deck.metricValues[id].toLocaleString()}</strong>
                    )}
                  </span>
                ))}
              </span>
            </div>
            <div className="impact-report-deck-inputs-row">
              <span className="impact-report-deck-inputs-label">Goals</span>
              {inputs.goals?.length ? (
                <ol className="impact-report-deck-goals">
                  {inputs.goals.map((goal) => (
                    <li key={goal}>{goal}</li>
                  ))}
                </ol>
              ) : (
                <span>None</span>
              )}
            </div>
          </div>
        </AccordionBody>
      </Accordion>
    </li>
  );
}
